import { CreditApplicationTemplate, isFormTemplateType } from '@dill/dill-shared';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle } from 'react-feather';
import { QrCode } from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  createSupplierCreditApplicationTemplate,
  getCreditApplicationTemplates,
} from '../../../../redux/services/creditApplicationsTemplatesService';
import { buildSupplierPath, openDillToast } from '../../../../utils/helpers';
import { DillColumn, DillTable } from '../../../general/Dill/DillTable';
import { DillButton, DillDropdownButton } from '../../../general/Dill';
import QRCodePrintModal from '../CreditApplicationComponents/QRCodePrintModal';
import { getCreditApplicationTemplateLabel } from './CONSTANTS';

const CreditApplicationTemplateTable = ({
  creditApplicationTemplates,
}: {
  creditApplicationTemplates: CreditApplicationTemplate[];
}) => {
  const dispatch = useAppDispatch();
  const [duplicating, setDuplicating] = useState('');
  const [qrUrl, setQrUrl] = useState<{ url: string; title: string } | null>(null);
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const isDillAdmin = user?.roles.includes('DILL_ADMIN') ?? false;

  const handleCopy = useCallback(
    async (text: string) => {
      try {
        const link = `${process.env.REACT_APP_FRONTEND_URL}/requestCreditApplication?domainId=${
          currentSupplier?.domainId ?? ''
        }&templateId=${text}`;
        await navigator.clipboard.writeText(link);
        openDillToast({
          message: 'Link copied to clipboard',
          type: 'SUCCESS',
        });
      } catch (err) {
        console.error('Failed to copy:', err);
        openDillToast({
          message: 'Link failed to copy',
          type: 'ERROR',
        });
      }
    },
    [currentSupplier?.domainId]
  );

  const handleDuplicateCreditTemplate = useCallback(
    async (data: CreditApplicationTemplate) => {
      setDuplicating(data.id);
      const { id, updatedAt, createdAt, ...rest } = data;
      const templateData = { ...rest };
      const response = await dispatch(
        createSupplierCreditApplicationTemplate({
          ...templateData,
          supplierId: currentSupplier?.id || '',
        })
      );
      if (
        response.type === 'creditApplicationTemplates/createCreditApplicationTemplate/fulfilled'
      ) {
        setDuplicating('');
        openDillToast({
          message: `Succcessfully ${data.applicationType
            .toLowerCase()
            .replace('_', ' ')} duplicated template`,
          type: 'SUCCESS',
        });
        await dispatch(
          getCreditApplicationTemplates({
            supplierId: currentSupplier?.id ?? '',
            applicationType: 'ALL',
          })
        );
      } else {
        setDuplicating('');
        openDillToast({
          message: `Failed to duplicate ${data.applicationType
            .toLowerCase()
            .replace('_', ' ')} template`,
          type: 'ERROR',
        });
      }
    },
    [dispatch, currentSupplier?.id]
  );

  const generalAppLink = `${process.env.REACT_APP_FRONTEND_URL}/requestCreditApplication?domainId=${
    currentSupplier?.domainId ?? ''
  }`;

  const handleGeneralLinkSelect = useCallback(
    async (option: { label: string; value: string }) => {
      if (option.value === 'link') {
        try {
          await navigator.clipboard.writeText(generalAppLink);
          openDillToast({ message: 'Link copied to clipboard', type: 'SUCCESS' });
        } catch {
          openDillToast({ message: 'Link failed to copy', type: 'ERROR' });
        }
      } else if (option.value === 'qr') {
        setQrUrl({ url: generalAppLink, title: 'General Application Link' });
      }
    },
    [generalAppLink]
  );

  const columns = useMemo<DillColumn<CreditApplicationTemplate>[]>(
    () => [
      {
        key: 'createdAt',
        label: 'Created At',
        width: 60,
        sortable: true,
        getSortValue: (row: CreditApplicationTemplate) =>
          row.createdAt ? new Date(row.createdAt).getTime().toString() : '',
        render: (_: any, row: CreditApplicationTemplate) => {
          if (!row.createdAt) return null;
          const date = new Date(row.createdAt);
          const formatted = date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          });
          return <p className="text-sm w-full">{formatted}</p>;
        },
      },
      {
        key: 'name',
        label: 'Name',
        width: 180,
        sortable: true,
        getSortValue: (row: CreditApplicationTemplate) => (row.name ?? '').toLowerCase(),
        render: (_: any, row: CreditApplicationTemplate) => (
          <p className="text-sm w-full truncate" title={row.name ?? ''}>
            {row.name || '-'}
          </p>
        ),
      },
      {
        key: 'applicationType',
        label: 'Application Type',
        width: 100,
        sortable: true,
        getSortValue: (row: CreditApplicationTemplate) =>
          getCreditApplicationTemplateLabel(row).toLowerCase(),
        render: (_: any, row: CreditApplicationTemplate) => (
          <p className="text-sm w-full">{getCreditApplicationTemplateLabel(row)}</p>
        ),
      },
      {
        key: 'action',
        label: 'Action',
        width: 350,
        render: (_: any, row: CreditApplicationTemplate) => (
          <div className="flex items-center gap-2">
            {isDillAdmin && (
              <DillButton
                variant="secondary"
                label="Edit"
                onClick={() => navigate(`${row.id}`)}
                height={32}
              />
            )}
            {!isFormTemplateType(row.applicationType) && isDillAdmin && (
              <DillButton
                variant="secondary"
                label="Duplicate Template"
                isLoading={duplicating === row.id}
                isDisabled={duplicating === row.id}
                onClick={() => handleDuplicateCreditTemplate(row)}
                height={32}
              />
            )}
            <DillButton
              variant="secondary"
              label={
                isFormTemplateType(row.applicationType) ? 'Preview Form' : 'Preview Application'
              }
              onClick={() =>
                navigate(
                  buildSupplierPath(
                    currentSupplier?.id,
                    `creditApplicationRequestTemplates/previewApplication/${row.id}`
                  )
                )
              }
              height={32}
            />
            {/* Internal / external forms have no public application link; they're sent from the
                Credit Applications page (Internal Forms > Send Form, or Send Link). */}
            {!isFormTemplateType(row.applicationType) && (
              <DillButton
                variant="primary"
                label="Get Template Link"
                onClick={() => handleCopy(row.id)}
                height={32}
              />
            )}
            {!isFormTemplateType(row.applicationType) && (
              <DillButton
                variant="icon-default"
                onClick={() => {
                  const link = `${
                    process.env.REACT_APP_FRONTEND_URL
                  }/requestCreditApplication?domainId=${
                    currentSupplier?.domainId ?? ''
                  }&templateId=${row.id}`;
                  setQrUrl({ url: link, title: getCreditApplicationTemplateLabel(row) });
                }}
                icon={<QrCode size={16} color={COLORS.PRIMARY_500} />}
                height={32}
                width={32}
              />
            )}
          </div>
        ),
      },
    ],
    [
      handleCopy,
      handleDuplicateCreditTemplate,
      duplicating,
      navigate,
      currentSupplier?.domainId,
      currentSupplier?.id,
      isDillAdmin,
    ]
  );

  const toolbarRight = (
    <div className="flex items-center gap-2">
      {creditApplicationTemplates.length > 0 && (
        <DillDropdownButton
          label="General App Link"
          variant="secondary"
          height={35}
          showChevron
          options={[
            { label: 'Get Link', value: 'link' },
            { label: 'QR Code', value: 'qr' },
          ]}
          onSelect={handleGeneralLinkSelect}
        />
      )}
      <DillButton
        variant="primary"
        label="Add New Template"
        onClick={() =>
          navigate(
            buildSupplierPath(
              currentSupplier?.id,
              `creditApplicationRequestTemplates/overview/addCreditApplicationRequestTemplate`
            )
          )
        }
        icon={<PlusCircle size={16} color={COLORS.WHITE} />}
        height={35}
      />
    </div>
  );

  return (
    <div className="w-full h-full">
      {qrUrl && (
        <QRCodePrintModal
          open={!!qrUrl}
          onClose={() => setQrUrl(null)}
          url={qrUrl.url}
          title={qrUrl.title}
        />
      )}
      <DillTable
        columns={columns}
        data={creditApplicationTemplates}
        rowKey={(row) => row.id}
        sortable
        pagination
        pageSize={20}
        search={false}
        striped={false}
        showCount={false}
        resizable
        emptyMessage="No credit/COD applications templates."
        toolbarRight={toolbarRight}
        rowHeight={64}
        countLabel="Templates"
      />
    </div>
  );
};

export default CreditApplicationTemplateTable;
